import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Shirt, ShoppingBag, Hash, Loader2, Gift, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_LABELS = {
  full_set: "Full set (jersey + shorts + socks)",
  jersey: "Jersey only",
  shorts: "Shorts only",
  socks: "Socks only",
};

export default function ParentKitOrdersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizeTarget, setSizeTarget] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kit/parent/my-kits`, { withCredentials: true });
      setData(r.data || { entitlements: [], config: null, children: [] });
    } catch (e) {
      setData({ entitlements: [], config: null, children: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const config = data?.config;
  const ents = data?.entitlements || [];
  const children = data?.children || [];

  // Group entitlements by player; merge child-list rows so kids without
  // entitlements still appear (replacement orders are always available).
  const byPlayer = {};
  for (const c of children) {
    byPlayer[c.id] = {
      player_id: c.id,
      player_name: c.display_name,
      jersey_number: c.jersey_number,
      team_id: c.team_id,
      age_group: c.age_group,
      entitlements: [],
    };
  }
  for (const e of ents) {
    const slot = byPlayer[e.player_id] || {
      player_id: e.player_id,
      player_name: e.player_name,
      jersey_number: e.jersey_number,
      entitlements: [],
    };
    slot.entitlements.push(e);
    if (e.jersey_number) slot.jersey_number = e.jersey_number;
    byPlayer[e.player_id] = slot;
  }

  const players = Object.values(byPlayer);
  // Initial-kit picks: kids who have a registration entitlement awaiting
  // size selection. Per Feb 2026 mandate, jersey assignment is decoupled
  // from size selection — parents can pick a size whether the academy has
  // assigned a jersey number yet or not. Both legacy
  // `pending_number_assignment` and the canonical `pending_size_selection`
  // are treated as "pick-a-size" states.
  const initialKitTodo = players
    .map((p) => ({
      player: p,
      entitlement: p.entitlements.find(
        (e) => e.entitlement_type === "registration" &&
               ["pending_size_selection", "pending_number_assignment"].includes(e.kit_status),
      ),
    }))
    .filter((x) => x.entitlement);

  // Display rows for already-confirmed initial kits (status badge)
  const initialKitConfirmed = players
    .map((p) => ({
      player: p,
      entitlement: p.entitlements.find(
        (e) => e.entitlement_type === "registration" &&
               ["size_selected", "ordered", "fulfilled"].includes(e.kit_status),
      ),
    }))
    .filter((x) => x.entitlement);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/academy" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="kit-orders-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Shirt className="w-9 h-9 text-fuchsia-400" /> Kit Orders
          </h1>
          <p className="text-slate-400 mt-1">Order your child&apos;s initial kit, or buy a replacement / extra kit.</p>
        </div>

        {loading && (
          <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400 mx-auto" /></div>
        )}

        {!loading && (
          <>
            {/* ─── Section 1 — Free initial kit ─── */}
            <Card className="border-emerald-500/30 bg-emerald-500/5 mb-6" data-testid="initial-kit-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-200">
                  <Gift className="w-5 h-5" /> Section 1 — Free initial kit
                </CardTitle>
                <p className="text-xs text-emerald-300/70 mt-1">
                  Included with the $148 registration fee — one free kit per child.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {initialKitTodo.length === 0 && initialKitConfirmed.length === 0 && (
                  <p className="text-sm text-slate-400 italic">
                    Your initial kit will appear here once your registration fee has been processed.
                  </p>
                )}

                {initialKitTodo.map(({ player, entitlement }) => (
                  <div key={player.player_id} className="bg-slate-900/40 border border-emerald-500/40 rounded p-3" data-testid={`initial-kit-row-${player.player_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-white">{player.player_name}</p>
                        {player.jersey_number ? (
                          <p className="text-xs text-fuchsia-300 flex items-center gap-1"><Hash className="w-3 h-3" /> Jersey #{player.jersey_number}</p>
                        ) : (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Hash className="w-3 h-3" /> Jersey number will be assigned by the academy
                          </p>
                        )}
                      </div>
                      <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">Action needed</Badge>
                    </div>
                    <p className="text-sm text-cyan-200 mb-1">📏 Select a kit size</p>
                    <p className="text-xs text-slate-400 mb-3">
                      No payment required — this is your child&apos;s included kit.
                      {!player.jersey_number && " You can pick the size now; the academy will assign the jersey number separately."}
                    </p>
                    <Button
                      size="sm"
                      onClick={() => setSizeTarget({ entitlement, config, player_name: player.player_name })}
                      data-testid={`select-size-btn-${player.player_id}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    >
                      Select size
                    </Button>
                  </div>
                ))}

                {initialKitConfirmed.map(({ player, entitlement }) => (
                  <div key={player.player_id} className="bg-slate-900/40 border border-slate-700 rounded p-3" data-testid={`initial-kit-confirmed-${player.player_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{player.player_name}</p>
                        {player.jersey_number && (
                          <p className="text-xs text-fuchsia-300 flex items-center gap-1"><Hash className="w-3 h-3" /> Jersey #{player.jersey_number}</p>
                        )}
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        Size: {entitlement.size_selected}
                      </Badge>
                    </div>
                    {entitlement.kit_status === "size_selected" && (
                      <p className="text-xs text-emerald-300 mt-1">✓ Size confirmed — we&apos;ll notify you when your kit is ready.</p>
                    )}
                    {entitlement.kit_status === "ordered" && (
                      <p className="text-xs text-blue-300 mt-1">📦 Ordered with supplier — will be ready for collection soon.</p>
                    )}
                    {entitlement.kit_status === "fulfilled" && (
                      <p className="text-xs text-emerald-400 mt-1">✅ Kit handed over.</p>
                    )}
                    <RemarksThread entitlement={entitlement} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* ─── Section 2 — Replacement / extra kit (paid) ─── */}
            <Card className="border-fuchsia-500/30 bg-fuchsia-500/5" data-testid="replacement-kit-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-fuchsia-200">
                  <ShoppingBag className="w-5 h-5" /> Section 2 — Replacement / extra kit
                </CardTitle>
                <p className="text-xs text-fuchsia-300/70 mt-1">
                  Lost or outgrown? Order a replacement or extra items, paid via PayNow.
                </p>
              </CardHeader>
              <CardContent>
                {players.length === 0 && (
                  <p className="text-sm text-slate-400 italic">
                    You&apos;ll be able to place replacement orders once you have a registered child in the academy.
                  </p>
                )}
                {players.map((player) => (
                  <div key={player.player_id} className="bg-slate-900/40 border border-slate-700 rounded p-3 mb-2" data-testid={`replacement-row-${player.player_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-white">{player.player_name}</p>
                        {player.jersey_number ? (
                          <p className="text-xs text-fuchsia-300 flex items-center gap-1"><Hash className="w-3 h-3" /> Jersey #{player.jersey_number}</p>
                        ) : (
                          <p className="text-xs text-amber-300/80 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Jersey number not yet assigned — academy will assign on fulfilment.</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOrderTarget({ player, config })}
                        data-testid={`order-additional-btn-${player.player_id}`}
                        className="border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                      >
                        <ShoppingBag className="w-3 h-3 mr-1" /> Order replacement / extra
                      </Button>
                    </div>

                    {/* History */}
                    {player.entitlements.filter((e) => e.entitlement_type === "additional").length > 0 && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer text-fuchsia-300/80 hover:text-fuchsia-200">
                          Past replacement orders ({player.entitlements.filter((e) => e.entitlement_type === "additional").length})
                        </summary>
                        <ul className="mt-1 space-y-0.5 ml-3 text-slate-400">
                          {player.entitlements
                            .filter((e) => e.entitlement_type === "additional")
                            .map((e) => (
                              <li key={e.id} className="font-mono text-[11px]">
                                {e.payment_reference || e.id.slice(0, 8)} · ${e.amount_due ?? "?"} · {e.kit_status} · size {e.size_selected || "—"}
                              </li>
                            ))}
                        </ul>
                      </details>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* ─── Size chart (always visible if config has one) ─── */}
            {config?.size_chart_image_url && (
              <Card className="bg-slate-800/40 border-slate-700 mt-6" data-testid="size-chart-card">
                <CardHeader><CardTitle className="text-slate-200 text-base">📐 Size chart</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400 italic mb-2">
                    The measurement stated is for guidance. Always try on the product to ensure that the size is correct.
                  </p>
                  <SizeChartWithFallback url={config.size_chart_image_url} />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {sizeTarget && (
        <SizeSelectionDialog
          data={sizeTarget}
          onClose={() => setSizeTarget(null)}
          onSubmitted={() => { setSizeTarget(null); reload(); }}
        />
      )}
      {orderTarget && (
        <OrderAdditionalDialog
          data={orderTarget}
          onClose={() => setOrderTarget(null)}
          onPlaced={() => { setOrderTarget(null); reload(); }}
        />
      )}
    </div>
  );
}

// Small conversation-trail widget showing the parent's remark and any admin
// reply. Read-only on the parent side; admin replies are added via the
// AdminKitManagement queue.
function RemarksThread({ entitlement }) {
  const p = entitlement?.parent_remarks;
  const a = entitlement?.admin_remark_reply;
  if (!p && !a) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="remarks-thread">
      {p && (
        <div className="text-[11px] px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 whitespace-pre-wrap break-words">
          <span className="opacity-60 mr-1">You:</span>{p}
        </div>
      )}
      {a && (
        <div className="text-[11px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 whitespace-pre-wrap break-words">
          <span className="opacity-60 mr-1">Academy:</span>{a}
        </div>
      )}
    </div>
  );
}

function SizeChartImg({ config }) {
  if (!config?.size_chart_image_url) return null;
  return (
    <div className="my-3 bg-slate-900 p-2 rounded">
      <p className="text-xs text-slate-400 italic mb-1">The measurement stated is for guidance. Always try on the product to ensure that the size is correct.</p>
      <SizeChartWithFallback url={config.size_chart_image_url} />
    </div>
  );
}

// Resilient size-chart <img> that swaps to a link fallback if the browser
// blocks the CDN (ad-blocker, corporate DNS filter, VPN content filter, etc).
// Also renders an always-visible "Open in new tab" link beneath the image
// so parents can still access the chart even when inline rendering fails.
function SizeChartWithFallback({ url }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="w-full max-w-2xl rounded border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200"
        data-testid="size-chart-fallback"
      >
        <div className="flex items-start gap-2 mb-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Size chart couldn&apos;t load inline.</p>
            <p className="text-xs text-amber-200/80">
              This is usually caused by an ad-blocker, VPN, or network filter blocking the image server.
            </p>
          </div>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-medium"
          data-testid="size-chart-open-new-tab"
        >
          📐 Open size chart in a new tab
        </a>
      </div>
    );
  }
  return (
    <>
      <img
        src={url}
        alt="Size chart"
        className="w-full max-w-2xl rounded"
        onError={() => setFailed(true)}
        data-testid="size-chart-img"
      />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-2 text-xs text-slate-400 hover:text-fuchsia-300 underline"
        data-testid="size-chart-open-link"
      >
        📐 Open size chart in a new tab
      </a>
    </>
  );
}

function SizeSelectionDialog({ data, onClose, onSubmitted }) {
  const { entitlement, config, player_name } = data;
  const [size, setSize] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parentRemarks, setParentRemarks] = useState("");

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/kit/parent/select-size`, {
        entitlement_id: entitlement.id,
        size,
        disclaimer_accepted: accepted,
        parent_remarks: parentRemarks.trim() || null,
      }, { withCredentials: true });
      toast.success("Kit size confirmed.");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            Select kit size — {player_name}
            {entitlement.jersey_number ? ` (#${entitlement.jersey_number})` : ""}
          </DialogTitle>
        </DialogHeader>
        <SizeChartImg config={config} />
        <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded text-xs text-amber-200 mb-3">
          {config?.disclaimer_text}
        </div>
        <div className="space-y-2">
          <label className="block text-sm">Size:
            <select value={size} onChange={(e) => setSize(e.target.value)} className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2" data-testid="kit-size-select">
              <option value="">— Choose a size —</option>
              {(config?.available_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span>Remarks <span className="text-slate-400 font-normal">(optional)</span></span>
            <textarea
              value={parentRemarks}
              onChange={(e) => setParentRemarks(e.target.value.slice(0, 300))}
              rows={2}
              placeholder="e.g., My child already has jersey #12 — please record it in the system. / Preferred pick-up time: weekends only. / Needs before Sat 22nd match."
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2 text-sm placeholder-slate-500"
              data-testid="kit-parent-remarks-input"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">{parentRemarks.length}/300 · Include the jersey number if one was already given to your child, or any other note for the academy.</p>
          </label>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="kit-disclaimer-checkbox" className="mt-0.5" />
            <span>I understand that my size selection is final and no exchanges will be provided.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!size || !accepted || busy} onClick={submit} data-testid="kit-size-submit-btn" className="bg-fuchsia-600 hover:bg-fuchsia-500">
            {busy ? "Submitting…" : "Confirm size"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderAdditionalDialog({ data, onClose, onPlaced }) {
  const { player, config } = data;
  const [items, setItems] = useState({ full_set: false, jersey: false, shorts: false, socks: false });
  const [size, setSize] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parentRemarks, setParentRemarks] = useState("");
  const [orderReason, setOrderReason] = useState("");
  const pricing = config?.pricing || {};
  const total = Object.entries(items).filter(([, v]) => v).reduce((sum, [k]) => sum + (pricing[k] || 0), 0);

  const submit = async () => {
    const chosen = Object.entries(items).filter(([, v]) => v).map(([k]) => ({ item_type: k }));
    if (!chosen.length) { toast.error("Select at least one item"); return; }
    if (!orderReason) { toast.error("Please select a reason for the order"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kit/parent/order-additional`, {
        player_id: player.player_id,
        items: chosen,
        size,
        disclaimer_accepted: accepted,
        parent_remarks: parentRemarks.trim() || null,
        order_reason: orderReason,
      }, { withCredentials: true });
      toast.success(`Order placed — ref ${r.data.payment_reference}.`);
      window.location.href = `/pay/${r.data.payment_reference}`;
      onPlaced();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            Replacement / additional kit — {player.player_name}
            {player.jersey_number ? ` (#${player.jersey_number})` : ""}
          </DialogTitle>
        </DialogHeader>
        {!player.jersey_number && (
          <p className="text-xs text-amber-300 italic">Your jersey number will be assigned by the academy when this order is fulfilled.</p>
        )}
        <SizeChartImg config={config} />
        <div className="space-y-2 my-3">
          <p className="text-sm font-medium">Items:</p>
          {[
            { key: "full_set", label: ITEM_LABELS.full_set },
            { key: "jersey", label: ITEM_LABELS.jersey },
            { key: "shorts", label: ITEM_LABELS.shorts },
            { key: "socks", label: ITEM_LABELS.socks },
          ].map((it) => (
            <label key={it.key} className="flex items-center justify-between bg-slate-800/50 p-2 rounded text-sm">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={items[it.key]}
                  onChange={(e) => setItems((s) => ({ ...s, [it.key]: e.target.checked }))}
                  data-testid={`kit-item-${it.key}`}
                />
                {it.label}
              </span>
              <span className="text-fuchsia-300 font-mono">${pricing[it.key] || 0}</span>
            </label>
          ))}
          <div className="flex justify-between items-center pt-2 border-t border-slate-700">
            <span className="font-medium">Total</span>
            <span className="text-xl font-bold text-fuchsia-300">${total}</span>
          </div>
          <label className="block text-sm">Size:
            <select value={size} onChange={(e) => setSize(e.target.value)} className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2" data-testid="kit-additional-size-select">
              <option value="">— Choose a size —</option>
              {(config?.available_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span>Reason for order <span className="text-rose-400">*</span></span>
            <select
              value={orderReason}
              onChange={(e) => setOrderReason(e.target.value)}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2"
              data-testid="kit-additional-reason-select"
            >
              <option value="">— Choose a reason —</option>
              <option value="outgrown">Outgrown — child has grown out of current kit</option>
              <option value="lost">Lost — kit misplaced</option>
              <option value="damaged">Damaged — kit torn or worn out</option>
              <option value="extra">Extra — spare set for laundry cycle / away games</option>
              <option value="other">Other — please explain in Remarks</option>
            </select>
            <p className="text-[10px] text-slate-500 mt-0.5">Helps the academy spot patterns and plan stock.</p>
          </label>
          <label className="block text-sm">
            <span>Remarks <span className="text-slate-400 font-normal">(optional)</span></span>
            <textarea
              value={parentRemarks}
              onChange={(e) => setParentRemarks(e.target.value.slice(0, 300))}
              rows={2}
              placeholder="e.g., My child already has jersey #12. / Need before Sat 22nd match. / Prefer name printed on jersey."
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2 text-sm placeholder-slate-500"
              data-testid="kit-additional-parent-remarks-input"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">{parentRemarks.length}/300 · Add jersey number if already known, tournament date, or any other note.</p>
          </label>
          <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded text-xs text-amber-200">
            {config?.disclaimer_text}
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="kit-additional-disclaimer-checkbox"
              className="mt-0.5"
            />
            <span>I understand that my size selection is final and no exchanges will be provided.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!size || !accepted || total <= 0 || busy}
            onClick={submit}
            data-testid="kit-additional-submit-btn"
            className="bg-fuchsia-600 hover:bg-fuchsia-500"
          >
            {busy ? "Creating…" : `Pay $${total} via PayNow`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
